import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorState } from './DataStates';

interface Props {
  children: ReactNode;
  /** Name of the route for logging (e.g., "Calendar") */
  routeName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Route-level error boundary that catches render errors within a single
 * route and shows a recovery UI without crashing the entire app shell.
 *
 * Unlike the global ErrorBoundary, this:
 * - Keeps the sidebar/shell intact
 * - Offers a retry button that resets the error state
 * - Doesn't require a full page reload
 *
 * @example
 * <Route path="/calendar" element={
 *   <RouteErrorBoundary routeName="Calendar">
 *     <Suspense fallback={<RouteSpinner />}>
 *       <CalendarRoute />
 *     </Suspense>
 *   </RouteErrorBoundary>
 * } />
 */
export class RouteErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[RouteErrorBoundary${this.props.routeName ? `: ${this.props.routeName}` : ''}]`,
      error,
      info.componentStack,
    );
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      // A stale chunk (new deploy) can only be fixed by a hard reload — just
      // clearing the error re-renders into the same dead import and loops.
      const isStaleChunk = /dynamically imported module|Failed to fetch|Importing a module/i.test(
        this.state.error?.message ?? '',
      );
      return (
        <div className="flex h-full items-center justify-center p-8">
          <ErrorState
            message={
              isStaleChunk
                ? 'New version available. Please reload the page.'
                : `Something went wrong${this.props.routeName ? ` in ${this.props.routeName}` : ''}.`
            }
            onRetry={isStaleChunk ? () => window.location.reload() : this.handleRetry}
          />
        </div>
      );
    }

    return this.props.children;
  }
}
